import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDeleteEmail } from '../../../hooks/apolloHooks/useDeleteEmail';
import { useDeletePhone } from '../../../hooks/apolloHooks/useDeletePhone';
import { BasicCustomerDataResponse, ContactDataResponse } from '../../../integrations/allkort/graphql/interface/Customer';
import { EMAIL_TYPES, filterEmailsByType } from '../../../utils/filterEmailsByType';
import { PHONE_TYPE_IDS } from '../../../utils/filterPhonesByType';
import { BadgeIcon, InfoIconOutlined, LightBulbIcon, PenIcon } from '../../svgs/ExpressonSVGs';
import RightsWrapper from '../RightsWrapper';
import Tooltip from '../Tooltip';
import ProfileDetailsEditMail from './ProfileDetailsEditMail';
import ProfileDetailsEditPhone from './ProfileDetailsEditPhone';

type ProfileDetailsProps = {
    customerData: BasicCustomerDataResponse
    customerContactData: ContactDataResponse
    refetchContactData?: () => void
}

/**
 * Converts Swedish phone numbers to international format
 * @param phoneNumber - Phone number string (e.g., "0708856464" or "011141212")
 * @returns Converted phone number with +46 prefix, the original if already in correct format, or false if invalid format
 * @example
 * convertToInternationalFormat("0708856464") // returns "+46708856464"
 * convertToInternationalFormat("011141212") // returns "+4611141212"
 * convertToInternationalFormat("+46708856464") // returns "+46708856464"
 */
const convertToInternationalFormat = (phoneNumber: string): string | false => {
    // Already in correct format
    if (phoneNumber.startsWith("+46")) {
        return phoneNumber;
    }
    // Needs conversion
    if (phoneNumber.startsWith("0")) {
        return `+46${phoneNumber.slice(1)}`;
    }
    // Invalid format
    return false;
};

const ProfileDetails: React.FC<ProfileDetailsProps> = ({ customerData, customerContactData, refetchContactData }) => {

    //console.log('Customer contact data in ProfileDetails:', customerContactData);

    const [clickCount, setClickCount] = useState(0)
    const [newEmail, setNewEmail] = useState(false)
    const [emailJustVerified, setEmailJustVerified] = useState(false)
    const [phoneJustVerified, setPhoneJustVerified] = useState(false)
    const { deleteEmail } = useDeleteEmail()
    const { deletePhone } = useDeletePhone()
    const [profileEditMode, setProfileEditMode] = useState(false)
    const profileDataRef = useRef<HTMLDivElement>(null)
    const [collapsedHeight, setCollapsedHeight] = useState<number | undefined>(undefined)

    const toggleEditMode = () => {
        if (!profileEditMode && profileDataRef.current) {
            // Capture exact current height before entering edit mode so CSS can transition from it
            setCollapsedHeight(profileDataRef.current.scrollHeight)
        }
        setProfileEditMode(prev => !prev)
    }

    // Filter emails by type - using Main Email type (ID: 6)
    const { found: hasMainEmail, emails: mainEmails } = filterEmailsByType(
        customerContactData?.currentCustomer?.emails,
        EMAIL_TYPES.MAIN_EMAIL
    );

    //console.log('[ProfileDetails] All emails:', customerContactData?.currentCustomer?.emails);

    const nationalId = customerContactData?.currentCustomer?.nationalId;
    const isCompany = customerData?.currentCustomer?.isCompany;
    const idLabel = isCompany ? "Organisationsnummer" : "Personnummer";

    // Measure collapsed height on mount and whenever data changes (screen resize not needed;
    // collapsedHeight is only read at the moment the user clicks edit, so it always captures current layout)
    useEffect(() => {
        if (!profileEditMode && profileDataRef.current) {
            setCollapsedHeight(profileDataRef.current.scrollHeight)
        }
    }, [customerContactData])

    //Render
    return (
        <div
            className={classNames(
                'Row__Inner',
                'profile-settings-profile-details',
                'white-pale-section',
                'shadow-one',
                'with-header'
            )}

        >
            <div className="header">
                <h2 className="body-text">
                    <span>
                        <BadgeIcon width={28} height={28} />
                    </span>
                    Profilinställningar
                </h2>

                <RightsWrapper
                    disableButtonIcon={<PenIcon width={18} height={18} />}
                    disableButtonText={'Redigera'}
                    onlySuperUser={true}
                    disableButtonTooltipText={'Du har inte behörighet att redigera profilinställningar.'}
                    notAButton={true} // Render a non-button element when rights are insufficient
                    disableButtonClasses={'disabled-edit-action'} // Custom class for styling the disabled state
                    toolTipPosition='top-end'
                >
                    <div
                        className={classNames("edit-action", { "disabled-edit-action": profileEditMode })}  //TODO: change the "edit-mode" against a state that handles if the user is in edit mode or not
                        onClick={!profileEditMode ? toggleEditMode : () => { return }}
                        tabIndex={!profileEditMode ? 0 : -1} // Only focusable when not in edit mode
                        onKeyDown={(e) => {
                            if (!profileEditMode && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                toggleEditMode();
                            }
                        }}
                    >
                        <PenIcon width={18} height={18} />
                        {profileEditMode ? 'Redigeringsläge' : 'Redigera'}
                    </div>
                </RightsWrapper>

            </div>
            <div className="content">
                <div
                    ref={profileDataRef}
                    className={classNames("profile-data", { "edit-mode": profileEditMode })}
                    onTransitionEnd={(e) => {
                        // Once the closing animation finishes, clear the explicit height so the
                        // element returns to natural flow height (handles content/data changes)
                        if (e.propertyName === 'height' && !profileEditMode) {
                            setCollapsedHeight(undefined)
                        }
                    }}
                >
                    {!profileEditMode ? (
                        <>
                            <div className="item name">
                                <span className="label">Namn</span>
                                <span className="value">
                                    {customerData.currentCustomer.name}
                                </span>
                            </div>

                            <div className="item customer-number">
                                <span className="label">Kundnummer</span>
                                <span className="value">
                                    {customerContactData?.currentCustomer?.customerNumber}
                                </span>
                            </div>

                            <div className={classNames("item",
                                "phone",
                                { "phone-verified-animation": phoneJustVerified })}>
                                <span className="label">Telefonnummer</span>
                                <span className="value">
                                    {(() => {
                                        const mainPhone = customerContactData?.currentCustomer?.phoneNumbers?.find(p => p.type?.id === PHONE_TYPE_IDS.MAIN)
                                        if (mainPhone) {
                                            return convertToInternationalFormat(mainPhone.number) || mainPhone.number
                                        }
                                        return (
                                            <span className="missing-info">
                                                Behöver kompletteras
                                                <Tooltip text="Klicka på redigera högst upp till höger för att fylla i ditt telefonnummer. Endast administratörer kan ändra detta." position="top">
                                                    <InfoIconOutlined width="26" height="26" />
                                                </Tooltip>
                                            </span>
                                        )
                                    })()}
                                </span>
                            </div>

                            <div className={classNames(
                                "item",
                                "email",
                                { "email-verified-animation": emailJustVerified })}>
                                <span className="label">E-post</span>
                                <span className="value">
                                    {hasMainEmail ? (
                                        <span >{mainEmails[0]}</span>
                                    ) : (
                                        <span className="missing-info">
                                            Behöver kompletteras
                                            <Tooltip text="Klicka på redigera högst upp till höger för att fylla i din e-postadress. Endast administratörer kan ändra detta." position="top">
                                                <InfoIconOutlined width="26" height="26" />
                                            </Tooltip>
                                        </span>
                                    )}
                                </span>
                            </div>

                            <div className="item national-id">
                                <span className="label">{idLabel}</span>
                                <span className="value">
                                    {isCompany ?
                                        (nationalId)
                                        : (
                                            `${nationalId?.slice(0, 8)}-${nationalId?.slice(8)}`
                                        )}
                                </span>
                            </div>

                            <div className="item address">
                                <span className="label">Adress</span>
                                <span className="value">
                                    {customerContactData?.currentCustomer?.address.city && customerContactData?.currentCustomer?.address.road && customerContactData?.currentCustomer?.address.postalCode ? (
                                        `${customerContactData.currentCustomer.address.road}, ${customerContactData.currentCustomer.address.postalCode} ${customerContactData.currentCustomer.address.city.charAt(0).toUpperCase() + customerContactData.currentCustomer.address.city.slice(1).toLowerCase()}`
                                    ) : (
                                        <>
                                            Ingen adress tillgänglig
                                            <Tooltip position='top-end' text="Din adress uppdateras automatiskt när ändring av folkbokföringsadress görs hos Skatteverket.">
                                                <InfoIconOutlined width={26} height={26} />
                                            </Tooltip>
                                        </>
                                    )}
                                </span>
                            </div>
                        </>
                    ) : (
                        <>
                            <ProfileDetailsEditMail
                                currentEmail={hasMainEmail ? mainEmails[0] : ''}
                                currentEmailId={customerContactData?.currentCustomer?.emails?.find(e => e.type.id === EMAIL_TYPES.MAIN_EMAIL)?.id}
                                currentEmailVerified={customerContactData?.currentCustomer?.emails?.find(e => e.type.id === EMAIL_TYPES.MAIN_EMAIL)?.verified ?? false}
                                customerId={customerData.currentCustomer.id}
                                onUpdateSuccess={(updatedEmail: string | undefined) => {
                                    console.log('Email updated successfully:', updatedEmail);
                                    toggleEditMode();
                                    setEmailJustVerified(true);
                                    setTimeout(() => setEmailJustVerified(false), 4000);
                                    setTimeout(() => refetchContactData?.(), 100);
                                }}
                                handleCancel={toggleEditMode}
                            />
                            <ProfileDetailsEditPhone
                                currentPhone={customerContactData?.currentCustomer?.phoneNumbers?.find(p => p.type?.id === PHONE_TYPE_IDS.MAIN)?.number ?? ''}
                                currentPhoneId={customerContactData?.currentCustomer?.phoneNumbers?.find(p => p.type?.id === PHONE_TYPE_IDS.MAIN)?.id}
                                currentPhoneVerified={customerContactData?.currentCustomer?.phoneNumbers?.find(p => p.type?.id === PHONE_TYPE_IDS.MAIN)?.verified ?? false}
                                customerId={customerData.currentCustomer.id}
                                onUpdateSuccess={(updatedPhone: string | undefined) => {
                                    console.log('Phone updated successfully:', updatedPhone);
                                    toggleEditMode();
                                    setPhoneJustVerified(true);
                                    setTimeout(() => setPhoneJustVerified(false), 4000);
                                    setTimeout(() => refetchContactData?.(), 100);
                                }}
                                handleCancel={toggleEditMode}
                            />
                        </>
                    )}


                </div>
                {true && (
                    <div className="info-message">
                        <span>
                            <LightBulbIcon width={28} height={28} />
                            Redigera kontaktuppgifter
                        </span>
                        <p>
                            Endast profiler med administratörsrättigheter kan redigera kontaktuppgifter. Se allas behörigheter under "Hantera användare" i menyn ovan.
                        </p>
                    </div>
                )}
            </div>
        </div >
    )
}

export default ProfileDetails;